import { loginResponseSchema, partnerMeDTOSchema, signInHandoffDTOSchema, signInHandoffExchangeDTOSchema, termsAcceptanceDTOSchema, } from '@instanexus/contracts';
import { z } from 'zod';
import { ApiError } from '../api-error.js';
import { dataEnvelopeSchema, WEB_API_PREFIX } from '../transport.js';
const loginEnvelopeSchema = dataEnvelopeSchema(loginResponseSchema);
const meEnvelopeSchema = dataEnvelopeSchema(partnerMeDTOSchema);
const termsEnvelopeSchema = dataEnvelopeSchema(termsAcceptanceDTOSchema);
const handoffEnvelopeSchema = dataEnvelopeSchema(signInHandoffDTOSchema);
const handoffExchangeEnvelopeSchema = dataEnvelopeSchema(signInHandoffExchangeDTOSchema);
const noContentSchema = z.undefined();
const PARTNER_AUTH_PATH = `${WEB_API_PREFIX}/partner/auth`;
export function createPartnerAuthResource(transport) {
    /** The refresh currently in flight, shared by every concurrent caller. */
    let refreshInFlight;
    async function requestRefresh() {
        try {
            const envelope = await transport.request({
                path: `${PARTNER_AUTH_PATH}/session`,
                schema: loginEnvelopeSchema,
                method: 'POST',
                // The cookie is the credential; no access token exists yet.
                auth: 'none',
            });
            return envelope.data;
        }
        catch (e) {
            // 401 is the normal "no live session" answer; anything else is a real failure and must
            // not masquerade as a signed-out partner.
            if (e instanceof ApiError && e.statusCode === 401)
                return undefined;
            throw e;
        }
    }
    return {
        async login(input) {
            const envelope = await transport.request({
                path: `${PARTNER_AUTH_PATH}/login`,
                schema: loginEnvelopeSchema,
                method: 'POST',
                body: input,
            });
            return envelope.data;
        },
        refreshSession() {
            if (refreshInFlight === undefined) {
                // Cleared on settle: dedupe covers only overlapping calls, never caches the answer.
                refreshInFlight = requestRefresh().finally(() => {
                    refreshInFlight = undefined;
                });
            }
            return refreshInFlight;
        },
        async logout() {
            await transport.request({
                path: `${PARTNER_AUTH_PATH}/logout`,
                schema: noContentSchema,
                method: 'POST',
                auth: 'none',
            });
        },
        async me() {
            const envelope = await transport.request({
                path: `${PARTNER_AUTH_PATH}/me`,
                schema: meEnvelopeSchema,
            });
            return envelope.data;
        },
        async acceptTerms() {
            const envelope = await transport.request({
                path: `${PARTNER_AUTH_PATH}/terms-acceptance`,
                schema: termsEnvelopeSchema,
                method: 'POST',
                body: { acceptTerms: true },
            });
            return envelope.data;
        },
        async acceptInvite(input) {
            await transport.request({
                path: `${PARTNER_AUTH_PATH}/accept-invite`,
                schema: noContentSchema,
                method: 'POST',
                body: input,
                auth: 'none',
            });
        },
        async issueHandoff(input) {
            const envelope = await transport.request({
                path: `${PARTNER_AUTH_PATH}/handoff`,
                schema: handoffEnvelopeSchema,
                method: 'POST',
                body: input,
                auth: 'none',
            });
            return envelope.data;
        },
        async exchangeHandoff(input) {
            const envelope = await transport.request({
                path: `${PARTNER_AUTH_PATH}/handoff/exchange`,
                schema: handoffExchangeEnvelopeSchema,
                method: 'POST',
                body: input,
                auth: 'none',
            });
            return envelope.data;
        },
    };
}
//# sourceMappingURL=partner-auth.js.map